import React, { useState, useEffect } from 'react';
import ModalPortal from './ModalPortal';
import { X, AlertCircle, CheckCircle, ArrowRight } from 'lucide-react';

const ResumeConflictDialog = ({
  isOpen,
  onClose,
  conflicts,
  onResolve
}) => {
  const [selections, setSelections] = useState({});

  // Ініціалізуємо вибір за замовчуванням (existing)
  useEffect(() => {
    if (isOpen && conflicts) {
      const defaultSelections = {};
      if (conflicts.name) {
        defaultSelections.name = 'existing';
      }
      if (conflicts.location) {
        defaultSelections.location = 'existing';
      }
      setSelections(defaultSelections);
    }
  }, [isOpen, conflicts]);

  const handleResolve = () => {
    if (onResolve) {
      onResolve(selections);
    }
  };

  const handleClose = () => {
    setSelections({});
    onClose();
  };

  if (!isOpen) {
    return null;
  }

  const hasSelections = Object.keys(selections).length > 0;
  const canResolve = hasSelections && Object.keys(conflicts || {}).every(
    key => selections[key] !== undefined
  );

  return (
    <ModalPortal onClose={handleClose} ariaLabel="Resume conflict">
      <div className="mx-4 flex max-h-[calc(100dvh-1.5rem)] w-full max-w-lg min-w-0 flex-col overflow-hidden rounded-lg bg-white shadow-xl dark:bg-gray-800 sm:max-h-[calc(100dvh-4rem)]">
        {/* Header */}
        <div className="flex shrink-0 flex-wrap items-center justify-between gap-3 border-b border-gray-200 p-4 dark:border-gray-700 sm:p-6">
          <div className="flex min-w-0 items-center gap-2">
            <AlertCircle className="w-5 h-5 text-yellow-500" />
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
              Resume Parser Conflicts
            </h3>
          </div>
          <button
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300 transition-colors"
            aria-label="Close dialog"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Content */}
        <div className="min-h-0 flex-1 overflow-y-auto p-4 sm:p-6">
          <div className="mb-4 p-4 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg">
            <p className="text-sm text-yellow-800 dark:text-yellow-300">
              The resume contains data that conflicts with existing form values. Please choose which values to keep.
            </p>
          </div>

          <div className="space-y-6">
            {/* Name Conflict */}
            {conflicts?.name && (
              <div className="bg-gray-50 dark:bg-gray-900/50 rounded-lg p-4 border border-gray-200 dark:border-gray-700">
                <label className="block text-sm font-semibold text-gray-900 dark:text-white mb-4">
                  Name Conflict:
                </label>

                {/* Visual comparison */}
                <div className="mb-4 p-3 bg-white dark:bg-gray-800 rounded border border-gray-200 dark:border-gray-600">
                  <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                    <div className="flex-1">
                      <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">Current value:</div>
                      <div className="font-medium text-gray-900 dark:text-white">{conflicts.name.existing}</div>
                    </div>
                    <ArrowRight className="w-5 h-5 text-gray-400 flex-shrink-0" />
                    <div className="flex-1">
                      <div className="text-xs text-blue-600 dark:text-blue-400 mb-1">From resume:</div>
                      <div className="font-medium text-blue-600 dark:text-blue-400">{conflicts.name.resume}</div>
                    </div>
                  </div>
                </div>

                <div className="space-y-2">
                  <label className={`flex items-center p-3 border-2 rounded-lg cursor-pointer transition-all ${
                    selections.name === 'existing'
                      ? 'border-green-500 bg-green-50 dark:bg-green-900/20'
                      : 'border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-800'
                  }`}>
                    <input
                      type="radio"
                      name="name"
                      value="existing"
                      checked={selections.name === 'existing'}
                      onChange={(e) => setSelections({...selections, name: e.target.value})}
                      className="mr-3 w-4 h-4 text-green-600 focus:ring-green-500"
                    />
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium text-gray-900 dark:text-white">
                          Keep current value
                        </span>
                        {selections.name === 'existing' && (
                          <CheckCircle className="w-4 h-4 text-green-600" />
                        )}
                      </div>
                      <div className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                        "{conflicts.name.existing}" (no changes)
                      </div>
                    </div>
                  </label>
                  <label className={`flex items-center p-3 border-2 rounded-lg cursor-pointer transition-all ${
                    selections.name === 'resume'
                      ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                      : 'border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-800'
                  }`}>
                    <input
                      type="radio"
                      name="name"
                      value="resume"
                      checked={selections.name === 'resume'}
                      onChange={(e) => setSelections({...selections, name: e.target.value})}
                      className="mr-3 w-4 h-4 text-blue-600 focus:ring-blue-500"
                    />
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium text-gray-900 dark:text-white">
                          Replace with resume data
                        </span>
                        {selections.name === 'resume' && (
                          <CheckCircle className="w-4 h-4 text-blue-600" />
                        )}
                      </div>
                      <div className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                        Change to "{conflicts.name.resume}"
                      </div>
                    </div>
                  </label>
                </div>
              </div>
            )}

            {/* Location Conflict */}
            {conflicts?.location && (
              <div className="bg-gray-50 dark:bg-gray-900/50 rounded-lg p-4 border border-gray-200 dark:border-gray-700">
                <label className="block text-sm font-semibold text-gray-900 dark:text-white mb-4">
                  Location Conflict:
                </label>

                {/* Visual comparison */}
                <div className="mb-4 p-3 bg-white dark:bg-gray-800 rounded border border-gray-200 dark:border-gray-600">
                  <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                    <div className="flex-1">
                      <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">Current value:</div>
                      <div className="font-medium text-gray-900 dark:text-white">{conflicts.location.existing}</div>
                    </div>
                    <ArrowRight className="w-5 h-5 text-gray-400 flex-shrink-0" />
                    <div className="flex-1">
                      <div className="text-xs text-blue-600 dark:text-blue-400 mb-1">From resume:</div>
                      <div className="font-medium text-blue-600 dark:text-blue-400">{conflicts.location.resume}</div>
                    </div>
                  </div>
                </div>

                <div className="space-y-2">
                  <label className={`flex items-center p-3 border-2 rounded-lg cursor-pointer transition-all ${
                    selections.location === 'existing'
                      ? 'border-green-500 bg-green-50 dark:bg-green-900/20'
                      : 'border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-800'
                  }`}>
                    <input
                      type="radio"
                      name="location"
                      value="existing"
                      checked={selections.location === 'existing'}
                      onChange={(e) => setSelections({...selections, location: e.target.value})}
                      className="mr-3 w-4 h-4 text-green-600 focus:ring-green-500"
                    />
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium text-gray-900 dark:text-white">
                          Keep current value
                        </span>
                        {selections.location === 'existing' && (
                          <CheckCircle className="w-4 h-4 text-green-600" />
                        )}
                      </div>
                      <div className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                        "{conflicts.location.existing}" (no changes)
                      </div>
                    </div>
                  </label>
                  <label className={`flex items-center p-3 border-2 rounded-lg cursor-pointer transition-all ${
                    selections.location === 'resume'
                      ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                      : 'border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-800'
                  }`}>
                    <input
                      type="radio"
                      name="location"
                      value="resume"
                      checked={selections.location === 'resume'}
                      onChange={(e) => setSelections({...selections, location: e.target.value})}
                      className="mr-3 w-4 h-4 text-blue-600 focus:ring-blue-500"
                    />
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium text-gray-900 dark:text-white">
                          Replace with resume data
                        </span>
                        {selections.location === 'resume' && (
                          <CheckCircle className="w-4 h-4 text-blue-600" />
                        )}
                      </div>
                      <div className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                        Change to "{conflicts.location.resume}"
                      </div>
                    </div>
                  </label>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="flex shrink-0 flex-col-reverse gap-3 border-t border-gray-200 p-4 pb-[max(1rem,env(safe-area-inset-bottom))] dark:border-gray-700 sm:flex-row sm:justify-end sm:p-6">
          <button
            onClick={handleClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleResolve}
            disabled={!canResolve}
            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
          >
            Apply Selected
          </button>
        </div>
      </div>
    </ModalPortal>
  );
};

export default ResumeConflictDialog;

